import React, { useCallback, useEffect, useState } from 'react';
import { TailSpin } from 'react-loader-spinner';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import MenuItem from '@mui/material/MenuItem';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import RefreshIcon from '@mui/icons-material/Refresh';
import Colors from '../../library/Colors';
import { surface, tone } from '../timeclock/tcFormat';
import { teamError, teamPost } from './teamFormat';
import TeamCalendarTab from './TeamCalendarTab';
import TeamMineTab from './TeamMineTab';
import TeamRequestTab from './TeamRequestTab';
import TeamApprovalsTab from './TeamApprovalsTab';
import TeamDutyTab from './TeamDutyTab';
import TeamWeekTab from './TeamWeekTab';
import TeamAdminTab from './TeamAdminTab';
import '../logging/logging.css';

/**
 * TeamScreen — Verlofplanner for SoftTouch Vlaanderen.
 *
 * Calendar, own leave (per person for management and Els), requests,
 * approvals, duty weeks with weekend swaps, the weekly schedule check and
 * management (balances, leave right, installations, hours, home days,
 * Excel import).
 *
 * Every call carries the team token that login hands to members of the
 * staff list; the backend takes the identity from that token only.
 */
const TeamScreen = (props) => {
    const t = props.t || ((k, d) => (typeof d === 'string' ? d : d?.defaultValue) || k);
    const lang = props.lang || 'nl';
    const token = props.login?.team_token || '';

    const [tabKey, setTabKeyState] = useState(() => {
        // Links in the planner's mails open a tab directly: .../team?tab=approve
        const fromLink = new URLSearchParams(window.location.search).get('tab');
        if (fromLink && ['calendar', 'mine', 'request', 'approve', 'duty', 'week', 'admin'].includes(fromLink)) {
            try { window.history.replaceState(null, '', window.location.pathname); } catch (e) { /* ignore */ }
            return fromLink;
        }
        try { return window.sessionStorage.getItem('team_tab_key') || 'calendar'; } catch (e) { return 'calendar'; }
    });
    const setTabKey = (value) => {
        try { window.sessionStorage.setItem('team_tab_key', value); } catch (e) { /* private mode */ }
        setTabKeyState(value);
    };

    const thisYear = new Date().getFullYear();
    const [year, setYear] = useState(thisYear);
    const [me, setMe] = useState(null);
    const [staff, setStaff] = useState([]);
    const [gateError, setGateError] = useState(token ? '' : t('team_err_no_token', 'The leave planner is only for SoftTouch Vlaanderen staff. If you are, log out and log in again.'));
    const [loadError, setLoadError] = useState('');
    const [reloadKey, setReloadKey] = useState(0);
    const [busy, setBusy] = useState(false);

    // A page of the SoftTouch support tool (like user-info and adminstats): its
    // own sidebar and full-width layout, also after a refresh.
    useEffect(() => {
        if (props.changeScreen) props.changeScreen('logging');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const frame = (children) => (
        <div className="_100perc log__main_container log__padding_sidebar">
            {/* The card is a flex item of the fixed-height column: without flex-shrink 0 it shrinks to the
                window and long content (year grid, management) runs past its bottom edge. */}
            <div className={'log__container_content ' + props.theme + ' container_content_' + props.theme + ' box_fade-in'}
                style={{ flexShrink: 0, boxSizing: 'border-box', marginBottom: 20 }}>
                <Box sx={{ p: { xs: 1, md: 2 } }}>{children}</Box>
            </div>
        </div>
    );

    const notify = useCallback((text, ok) => {
        if (props.notify) props.notify(text, 5000, 'top-right', props.theme, ok ? 1 : 2);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.theme]);

    /** A failed call: an expired session closes the page, anything else is shown in place. */
    const failed = useCallback((response) => {
        if (response?.translation_key === 'team_session_invalid') {
            setGateError(teamError(t, response));
            return '';
        }
        return teamError(t, response);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Who am I (rights, waiting counts), then the staff list: one request at a time.
    useEffect(() => {
        if (!token) return;
        let alive = true;
        setBusy(true);
        teamPost('me', token).then((response) => {
            if (!alive) return;
            if (!response?.success) {
                setBusy(false);
                setLoadError(failed(response));
                return;
            }
            setMe(response.data);
            teamPost('staff', token).then((res) => {
                if (!alive) return;
                setBusy(false);
                if (res?.success) {
                    setStaff(res.data || []);
                    setLoadError('');
                } else {
                    setLoadError(failed(res));
                }
            });
        });
        return () => { alive = false; };
    }, [token, reloadKey, failed]);

    const changed = useCallback(() => setReloadKey((k) => k + 1), []);

    const s = surface(props.theme);
    const rights = me?.rights || {};
    const badge = (n) => (n > 0 ? (
        <Box component="span" sx={{ fontSize: 11.5, fontWeight: 700, color: '#fff', backgroundColor: tone.bad, borderRadius: '10px', px: 0.8, lineHeight: '18px', ml: 0.75 }}>{n}</Box>
    ) : null);
    const tabs = [
        { key: 'calendar', label: t('team_tab_calendar', 'Calendar') },
        { key: 'mine', label: rights.sees_all ? t('team_tab_per_person', 'Leave per person') : t('team_tab_mine', 'My leave') },
        { key: 'request', label: t('team_tab_request', 'Request') },
    ];
    if (rights.approve) tabs.push({ key: 'approve', label: t('team_tab_approve', 'Approve'), badge: me?.waiting });
    tabs.push({ key: 'duty', label: t('team_tab_duty', 'Duty week'), badge: me?.swaps_to_answer });
    tabs.push({ key: 'week', label: t('team_tab_week', 'Weekly schedule') });
    if (rights.sees_all || rights.import || rights.manager) tabs.push({ key: 'admin', label: t('team_tab_admin', 'Management') });
    const current = tabs.find((x) => x.key === tabKey) ? tabKey : 'calendar';

    const header = (
        <Box sx={{ display: 'flex', alignItems: 'center', columnGap: 2, rowGap: 1.5, mb: 2, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mr: 'auto', minWidth: 0 }}>
                <Box sx={{ width: 40, height: 40, borderRadius: '8px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: Colors.sft_blue, color: '#fff' }}>
                    <EventAvailableIcon />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                    <Typography component="h1" sx={{ fontSize: 21, fontWeight: 700, lineHeight: 1.25 }}>{t('team_page_title', 'Leave planner')}</Typography>
                    <Typography sx={{ fontSize: 13, color: 'text.secondary', lineHeight: 1.4 }}>
                        {me?.staff?.name
                            ? 'SoftTouch Vlaanderen · ' + me.staff.name + (rights.manager ? ' · ' + t('team_role_manager', 'management') : rights.sees_all ? ' · ' + t('team_role_admin', 'administration') : '')
                            : 'SoftTouch Vlaanderen'}
                    </Typography>
                </Box>
            </Box>
            {me && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Button variant="text" onClick={changed} disabled={busy}
                        startIcon={busy ? <CircularProgress size={16} /> : <RefreshIcon />}
                        sx={{ textTransform: 'none', height: 40, px: 1.25, color: 'text.secondary', fontWeight: 400 }}>
                        {t('tc_refresh', 'Refresh')}
                    </Button>
                    {(current === 'mine' || current === 'admin') && (
                        <TextField select size="small" label={t('team_year', 'Year')} value={year} onChange={(e) => setYear(e.target.value)}
                            sx={{ width: 110, '& .MuiInputBase-root': { height: 40 } }}>
                            {[thisYear - 1, thisYear, thisYear + 1].map((y) => <MenuItem key={y} value={y}>{y}</MenuItem>)}
                        </TextField>
                    )}
                    {current !== 'request' && (
                        <Button variant="contained" disableElevation startIcon={<AddIcon />} onClick={() => setTabKey('request')}
                            sx={{ textTransform: 'none', height: 40 }}>
                            {t('team_new_request', 'Request leave')}
                        </Button>
                    )}
                </Box>
            )}
        </Box>
    );

    if (gateError) {
        return frame(
            <>
                {header}
                <Box sx={{ ...s.card, p: 3, maxWidth: 640 }}>
                    <Typography sx={{ fontSize: 15, fontWeight: 700, mb: 0.5 }}>{t('team_no_access_title', 'The leave planner is not available')}</Typography>
                    <Typography sx={{ fontSize: 14, color: 'text.secondary' }}>{gateError}</Typography>
                </Box>
            </>
        );
    }

    if (!me || !staff.length) {
        return frame(
            <>
                {header}
                {loadError ? (
                    <Box sx={{ py: 4 }}>
                        <Typography sx={{ color: tone.bad, fontSize: 14, mb: 1.5 }}>{loadError}</Typography>
                        <Button variant="outlined" size="small" onClick={changed} sx={{ textTransform: 'none' }}>{t('tc_retry', 'Try again')}</Button>
                    </Box>
                ) : (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                        <TailSpin height="40" width="40" color={Colors.sft_blue} ariaLabel="loading" />
                    </Box>
                )}
            </>
        );
    }

    const common = {
        t, lang, theme: props.theme, token, me, staff, reloadKey, notify, failed, year,
        onChanged: changed, onImported: changed,
    };

    return frame(
        <>
            {header}
            <Tabs value={current} onChange={(e, v) => setTabKey(v)} variant="scrollable" scrollButtons="auto"
                sx={{ mb: 2.5, minHeight: 44, borderBottom: s.line, '& .MuiTab-root': { textTransform: 'none', minHeight: 44, fontSize: 14.5, fontWeight: 600, px: 2 } }}>
                {tabs.map((x) => <Tab key={x.key} value={x.key} label={<Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>{x.label}{badge(x.badge)}</Box>} />)}
            </Tabs>

            {loadError && <Typography sx={{ color: tone.bad, fontSize: 13.5, mb: 1 }}>{loadError}</Typography>}

            {current === 'calendar' && <TeamCalendarTab {...common} />}
            {current === 'mine' && <TeamMineTab {...common} />}
            {current === 'request' && <TeamRequestTab {...common} />}
            {current === 'approve' && <TeamApprovalsTab {...common} />}
            {current === 'duty' && <TeamDutyTab {...common} />}
            {current === 'week' && <TeamWeekTab {...common} />}
            {current === 'admin' && <TeamAdminTab {...common} />}
        </>
    );
};


export default TeamScreen;
